/**
 * News / Event Detail Page
 * Full article or event view with related content
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Clock, Share2, Bookmark } from 'lucide-react';
import axios from 'axios';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import NewsAndEventsHero from '../components/NewsAndEventsHero';
import banner1 from '../../../assets/images/banner1.png';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const AUTH_USERNAME = process.env.REACT_APP_USERNAME || '';
const AUTH_PASSWORD = process.env.REACT_APP_PASSWORD || '';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
};

const getCategoryColor = (cat) => {
  const palette = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-orange-100 text-orange-700',
    'bg-purple-100 text-purple-700',
    'bg-red-100 text-red-700',
    'bg-teal-100 text-teal-700',
    'bg-indigo-100 text-indigo-700',
    'bg-pink-100 text-pink-700',
    'bg-cyan-100 text-cyan-700',
    'bg-amber-100 text-amber-700',
    'bg-lime-100 text-lime-700',
    'bg-sky-100 text-sky-700',
  ];
  if (!cat) return 'bg-gray-100 text-gray-700';
  let hash = 0;
  for (let i = 0; i < cat.length; i++) {
    hash = cat.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
};

const NewsDetailPage = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}media/get`,
          { MediaId: parseInt(id, 10) },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
            auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
          }
        );

        if (response.data?.code === 1 && response.data?.data) {
          const raw = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
          if (raw && raw.IsDeleted !== true && raw.IsPublished === true) {
            setItem({
              id: String(raw.MediaId ?? raw.Id ?? id),
              type: (raw.Type ?? 'news').toString().toLowerCase(),
              category: raw.Category ?? raw.MediaSourceType ?? '',
              title: stripHtml(raw.Title ?? ''),
              summary: stripHtml(raw.Summary ?? raw.Description ?? ''),
              content: raw.Content ?? raw.Body ?? raw.FullDescription ?? raw.Details ?? raw.Description ?? '',
              date: formatDate(raw.PublishedAt ?? raw.CreatedAt),
              author: raw.Author ?? raw.CreatedBy ?? '',
              image: raw.Image ?? raw.ImageUrl ?? raw.Link ?? null,
              eventDate: formatDate(raw.EventDate),
              location: raw.Location ?? '',
              time: raw.Time ?? '',
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id]);

  useEffect(() => {
    if (!item) return;
    const fetchRelated = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}media/by-filter`,
          { CategoryId: 58, Type: 'LATEST-UPDATES' },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
            auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
          }
        );
        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          const items = response.data.data
            .filter((d) => d.IsDeleted !== true && d.IsPublished === true && String(d.MediaId ?? d.Id) !== id)
            .slice(0, 3)
            .map((d) => ({
              id: String(d.MediaId ?? d.Id),
              title: stripHtml(d.Title ?? ''),
              image: d.Image ?? d.ImageUrl ?? d.Link ?? null,
              date: formatDate(d.PublishedAt ?? d.CreatedAt),
              category: d.Category ?? d.MediaSourceType ?? '',
            }));
          setRelated(items);
        }
      } catch (error) {
        console.error('Failed to fetch related:', error);
      }
    };
    fetchRelated();
  }, [item, id]);

  if (loading) {
    return (
      <div>
        <NewsAndEventsHero />
        <div className="bg-gray-50 pb-16">
          <div className="container mx-auto px-6 md:px-8 lg:px-12 py-10">
            <div className="max-w-4xl mx-auto space-y-4">
              <div className="h-64 bg-gray-200 rounded-2xl animate-pulse" />
              <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
              <div className="h-32 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div>
        <NewsAndEventsHero />
        <div className="container mx-auto px-6 py-20 text-center">
          <p className="text-gray-400 text-sm">Article or event not found.</p>
          <Link
            to="/news-events"
            className="inline-flex items-center mt-4 text-[#ca9a37] text-sm font-semibold hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to News & Events
          </Link>
        </div>
      </div>
    );
  }

  const isEvent = item.type === 'event';
  const badgeClass = getCategoryColor(item.category);

  return (
    <div>
      <NewsAndEventsHero />

      <article className="bg-gray-50 pb-16">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          {/* Back Link */}
          <div className="py-5">
            <Link
              to="/news-events"
              className="inline-flex items-center text-sm text-gray-500 hover:text-[#ca9a37] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back to News & Events
            </Link>
          </div>

          {/* Article Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden max-w-4xl mx-auto">
            {/* Hero Image */}
            <div className="relative h-64 md:h-80 overflow-hidden">
              <img
                src={item.image || banner1}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>
                  {item.category}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-10">
              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-4 leading-snug">
                {item.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-6 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {item.date}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {item.category}
                </span>
                {isEvent && (
                  <>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {item.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {item.time}
                    </span>
                  </>
                )}
              </div>

              {/* Event Date Badge */}
              {isEvent && item.eventDate && (
                <div className="mb-6 p-4 bg-[#f8f1e5] rounded-xl border border-[#ca9a37]/20">
                  <div className="flex items-center gap-2 text-[#ca9a37] font-semibold text-sm">
                    <Calendar className="w-5 h-5" />
                    Event Date: {item.eventDate}
                  </div>
                </div>
              )}

              {/* Article Body */}
              <div className="text-gray-600 leading-relaxed break-words overflow-hidden [&_img]:max-w-full [&_img]:h-auto [&_p]:mb-4">
                {item.content ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }} />
                ) : (
                  <p className="text-base">{item.summary}</p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex items-center gap-3">
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
            </div>
          </div>

          {/* Related Content */}
          {related.length > 0 && (
            <div className="mt-12 max-w-4xl mx-auto">
              <h2 className="text-lg font-bold text-[#2f3951] mb-5">Related Updates</h2>
              <div className="grid md:grid-cols-3 gap-5">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/news-events/${rel.id}`}
                    className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                  >
                    <div className="h-32 overflow-hidden">
                      <img
                        src={rel.image || banner1}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-xs font-bold text-[#2f3951] line-clamp-2 group-hover:text-[#ca9a37] transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-[11px] text-gray-400 mt-1.5">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default NewsDetailPage;
